import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { devicesApi } from './devices.api';
import { deviceKeys } from './devices.queries';
import { handoverScanningKeys } from '@/features/handover-scanning';
import type {
  CreateDeviceRequest,
  UpdateDeviceRequest,
  DevicesListParams
} from '../types';

export function useCreateDeviceMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('devices');

  return useMutation({
    mutationFn: (data: CreateDeviceRequest) => devicesApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useUpdateDeviceMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('devices');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateDeviceRequest }) =>
      devicesApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useDeleteDeviceMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('devices');

  return useMutation({
    mutationFn: (id: number) => devicesApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useBulkDeleteDevicesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('devices');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<DevicesListParams> }
    ) => devicesApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}
